import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { ApiSettings } from '../config/api-settings';
import { SSO_PROVIDERS, SsoProviderId } from '../config/sso-providers';
import { User, Organization, Wallet } from '../models';
import { OrgContextService } from './org-context.service';
import { SessionService } from './session.service';

export interface SignInResult {
  user: User;
  organization: Organization;
}

/** What sign_in returns instead of a SignInResult when the account has 2FA on
 *  the challenge_token is short-lived (expiresIn seconds) and gets handed back
 *  to completeTwoFactorChallenge() along with the code to actually finish signing in. */
export interface TwoFactorChallenge {
  twoFactorRequired: true;
  challengeToken: string;
  expiresIn: number;
}

export interface SignUpArgs {
  email: string;
  password: string;
  passwordConfirmation: string;
  name: string;
  organizationName?: string;
}

export interface SignUpResult {
  user: User;
  organization: Organization;
  joinedExistingOrganization: boolean;
  wallet: Wallet;
  message: string;
}

export interface ConfirmEmailResult {
  user: User;
  message: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  constructor(
    private http: HttpClient,
    private orgContext: OrgContextService,
    private session: SessionService,
  ) {}

  /** No token to read out of the response and store anymore the backend
   *  sets the session as an httpOnly cookie directly on this response,
   *  which the browser handles entirely on its own. This call's job is
   *  just to trigger that and hand back who signed in unless the account
   *  has 2FA on, in which case there's no cookie yet and the caller has to
   *  collect a code and finish through completeTwoFactorChallenge(). */
  login(args: { email: string; password: string }): Observable<SignInResult | TwoFactorChallenge> {
    const URL = `${ApiSettings.fullPath}auth/sign_in`;

    return this.http.post<any>(URL, { user: args }).pipe(
      map((res): SignInResult | TwoFactorChallenge => res.two_factor_required
        ? { twoFactorRequired: true, challengeToken: res.challenge_token, expiresIn: res.expires_in }
        : { user: new User(res.user), organization: new Organization(res.organization) }),
      tap(result => {
        // Not actually signed in yet until the challenge below completes.
        if (!('twoFactorRequired' in result)) this.session.invalidateAuthCheck();
      }),
    );
  }

  /** Completes sign-in after login() comes back asking for a 2FA code.
   *  Endpoint/body shape inferred from this backend's other /auth/* and
   *  /me/two_factor conventions (POST, flat body, no `user:` wrapper), not
   *  yet confirmed against the real response if this 404s or the fields
   *  differ, check the Network tab the way we did for /me/two_factor and
   *  adjust the mapping below. */
  completeTwoFactorChallenge(args: { challengeToken: string; code: string }): Observable<SignInResult> {
    const URL = `${ApiSettings.fullPath}auth/two_factor`;

    const body = { challenge_token: args.challengeToken, code: args.code };

    return this.http.post<any>(URL, body).pipe(
      map(res => ({
        user: new User(res.user),
        organization: new Organization(res.organization),
      })),
      tap(() => this.session.invalidateAuthCheck()),
    );
  }

  signup(args: SignUpArgs): Observable<SignUpResult> {
    const URL = `${ApiSettings.fullPath}auth/sign_up`;

    const body = {
      user: {
        email: args.email,
        password: args.password,
        password_confirmation: args.passwordConfirmation,
        name: args.name,
        organization_name: args.organizationName,
      },
    };

    return this.http.post<any>(URL, body).pipe(
      map(res => ({
        user: new User(res.user),
        organization: new Organization(res.organization),
        joinedExistingOrganization: !!res.joined_existing_organization,
        wallet: new Wallet(res.wallet),
        message: res.message,
      })),
    );
  }

  confirmEmail(token: string): Observable<ConfirmEmailResult> {
    const URL = `${ApiSettings.fullPath}auth/confirmation`;
    const params = new HttpParams().set('confirmation_token', token);

    return this.http.get<any>(URL, { params }).pipe(
      map(res => ({
        user: new User(res.user),
        message: res.message,
      })),
    );
  }

  resendConfirmation(email: string): Observable<{ message: string }> {
    const URL = `${ApiSettings.fullPath}auth/confirmation`;

    return this.http.post<any>(URL, { user: { email } }).pipe(
      map(res => ({ message: res.message })),
    );
  }

  requestPasswordReset(email: string): Observable<{ message: string }> {
    const URL = `${ApiSettings.fullPath}auth/password`;

    return this.http.post<any>(URL, { user: { email } }).pipe(
      map(res => ({ message: res.message })),
    );
  }

  resetPassword(args: {
    resetPasswordToken: string;
    password: string;
    passwordConfirmation: string;
  }): Observable<{ message: string }> {
    const URL = `${ApiSettings.fullPath}auth/password`;

    const body = {
      user: {
        reset_password_token: args.resetPasswordToken,
        password: args.password,
        password_confirmation: args.passwordConfirmation,
      },
    };

    return this.http.put<any>(URL, body).pipe(
      map(res => ({ message: res.message })),
    );
  }

  /** The DELETE call itself is what actually invalidates the session
   *  it's what tells the backend to clear/expire the cookie. Clearing
   *  local state without this call would just be cosmetic. */
  logout(): Observable<void> {
    const URL = `${ApiSettings.fullPath}auth/sign_out`;

    return this.http.delete<void>(URL).pipe(
      catchError(() => of(undefined)),
      tap(() => this.clearSession()),
    );
  }

  clearSession(): void {
    this.orgContext.clear();
    this.session.clear();
  }

  startSso(provider: SsoProviderId, returnTo = '/'): void {
    const { path } = SSO_PROVIDERS.find(p => p.id === provider)!;
    const params = new HttpParams().set('return_to', `${window.location.origin}${returnTo}`);
    window.location.href = `${ApiSettings.fullPath}${path}?${params.toString()}`;
  }
}
